import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Button } from 'ui-components/components/button';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';

import ModalPortal from '../../../../../../../components/modal-portal';
import { initialFreightConfigurationDefaults } from '../../../../constants';
import { ScheduleFreightEdit } from './schedule-freight-edit';
import { useYup } from '../../../../../../../validation/use-yup';
import { FreightConfigurationSchema } from './form-schema';
import styles from './schedule-freight.module.less';
import { messages } from './messages';
import { useAdvancedShippingNoticeContext } from '../../advanced-shipping-notice-context';

import { DROP_OFF_OPTION_ONE, DROP_OFF_OPTION_TWO, DROP_OFF_OPTION_THREE } from '../constants';

const parseDateTimeString = (dateTimeString) => {
  if (!dateTimeString) return { date: '', time: '' };

  const dateTime = moment.utc(dateTimeString);
  return {
    date: dateTime.format('MM/DD/YYYY'),
    time: dateTime.format('h:mmA'),
  };
};

const createFormState = (freightConfiguration) => {
  return {
    confirmedDropOffDate: freightConfiguration.confirmedDropOffDate,
    carrierContact: freightConfiguration.carrierContact,
    tenantContact: freightConfiguration.tenantContact,
    deliveryNote: freightConfiguration.deliveryNote,
    dropOffDateOptionOne: parseDateTimeString(freightConfiguration[DROP_OFF_OPTION_ONE]).date,
    dropOffDateOptionTwo: parseDateTimeString(freightConfiguration[DROP_OFF_OPTION_TWO]).date,
    dropOffDateOptionThree: parseDateTimeString(freightConfiguration[DROP_OFF_OPTION_THREE]).date,
    timePickerOne: parseDateTimeString(freightConfiguration[DROP_OFF_OPTION_ONE]).time,
    timePickerTwo: parseDateTimeString(freightConfiguration[DROP_OFF_OPTION_TWO]).time,
    timePickerThree: parseDateTimeString(freightConfiguration[DROP_OFF_OPTION_THREE]).time,
  };
};

const convertBackToFreightConfiguration = (formState) => {
  return {
    [DROP_OFF_OPTION_ONE]: moment.utc(`${formState.dropOffDateOptionOne} ${formState.timePickerOne}`, 'MM/DD/YYYY hh:mmA').format(),
    [DROP_OFF_OPTION_TWO]: moment.utc(`${formState.dropOffDateOptionTwo} ${formState.timePickerTwo}`, 'MM/DD/YYYY hh:mmA').format(),
    [DROP_OFF_OPTION_THREE]: moment.utc(`${formState.dropOffDateOptionThree} ${formState.timePickerThree}`, 'MM/DD/YYYY hh:mmA').format(),
    confirmedDropOffDate: formState.confirmedDropOffDate,
    carrierContact: formState.carrierContact,
    tenantContact: formState.tenantContact,
    deliveryNote: formState.deliveryNote,
  };
};

export const ScheduleFreightCard = ({ closeModal }) => {
  const { onSubmitFreightConfiguration, freightConfiguration } = useAdvancedShippingNoticeContext();
  const [formState, setFormState] = useState(() => createFormState(freightConfiguration ?? initialFreightConfigurationDefaults));

  const { errors, validate } = useYup({});

  const onHandleSubmit = useCallback(() => {
    const isValid = validate(formState, FreightConfigurationSchema);

    if (!isValid) {
      return;
    }

    const didSucceed = onSubmitFreightConfiguration(convertBackToFreightConfiguration(formState));
    if (didSucceed) {
      closeModal();
    }
  }, [closeModal, formState, onSubmitFreightConfiguration, validate]);
  return (
    <ModalPortal onClose={closeModal}>
      <Modal size='large'>
        <ModalHeader>
          <FormattedMessage {...messages.scheduleFreightTitle} />
        </ModalHeader>
        <ModalBody className={styles['modal-body']}>
          <ScheduleFreightEdit errors={errors} formState={formState} setFormState={setFormState} />
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton closeModal={closeModal} />
          <Button onClick={onHandleSubmit} theme='primary'>
            <FormattedMessage {...messages.submitButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
